import { API_ROOT } from "config.js";
import { getAccessToken } from "containers/authenticated";

const LEGACY_HOST = "connect.hedera.online";
const BETA_HOST = "connect-beta.netlify.app";
const LEGACY_URL = `https://${LEGACY_HOST}`;
const BETA_URL = `https://${BETA_HOST}`;
const PREFERENCE_KEY = "hconnect_preferred_app_version";
const PROMPT_SHOWN_KEY = "hconnect_beta_prompt_shown";
const PREFERENCE_PARAM = "hconnect_preferred_app_version";
const TRACKING_ENDPOINT = `${API_ROOT}/api/version-preference/`;

function consumePreferenceFromUrl() {
  if (!isLegacyHost()) {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const versionFromParam = searchParams.get(PREFERENCE_PARAM);

  if (versionFromParam === "legacy") {
    localStorage.removeItem(PREFERENCE_KEY);
    localStorage.setItem(PROMPT_SHOWN_KEY, "true");
  } else if (versionFromParam === "beta") {
    localStorage.setItem(PREFERENCE_KEY, "beta");
    localStorage.setItem(PROMPT_SHOWN_KEY, "true");
  }

  if (versionFromParam !== null) {
    searchParams.delete(PREFERENCE_PARAM);
    const nextSearch = searchParams.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", nextUrl);
  }

  return versionFromParam;
}

function sanitizeMetadata(metadata) {
  if (!metadata || typeof metadata !== "object") {
    return {};
  }

  if (metadata.target || metadata.currentTarget || typeof metadata.preventDefault === "function") {
    return {};
  }

  try {
    JSON.stringify(metadata);
    return metadata;
  } catch (error) {
    return {};
  }
}

function postPreference(payload) {
  const token = getAccessToken();
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  let body;
  try {
    body = JSON.stringify(payload);
  } catch (error) {
    body = JSON.stringify({
      ...payload,
      metadata: {
        trigger: "tracking_payload_serialization_error",
      },
    });
  }

  fetch(TRACKING_ENDPOINT, {
    method: "POST",
    credentials: "include",
    headers,
    body,
    keepalive: true,
  }).catch(() => {
    // Intentionally ignore tracking failures so navigation is not blocked.
  });
}

export function isLegacyHost() {
  return window.location.hostname === LEGACY_HOST;
}

export function isBetaHost() {
  return window.location.hostname === BETA_HOST;
}

export function redirectToBetaIfOptedIn() {
  if (!isLegacyHost()) {
    return false;
  }

  consumePreferenceFromUrl();

  const preferredVersion = localStorage.getItem(PREFERENCE_KEY);
  if (preferredVersion !== "beta") {
    return false;
  }

  const destinationUrl = `${BETA_URL}${window.location.pathname}${window.location.search}${window.location.hash}`;
  postPreference({
    selected_version: "beta",
    source_app: "legacy",
    destination_url: destinationUrl,
    metadata: {
      trigger: "persistent_preference",
    },
  });

  window.location.replace(destinationUrl);
  return true;
}

export function promptForBetaOptIn() {
  if (!isLegacyHost()) {
    return;
  }

  const hasPromptBeenShown = localStorage.getItem(PROMPT_SHOWN_KEY) === "true";
  if (hasPromptBeenShown) {
    return;
  }

  localStorage.setItem(PROMPT_SHOWN_KEY, "true");

  const selected = window.confirm(
    "A new beta version of Hedera Connect is available. Do you want to switch now?"
  );

  if (selected) {
    localStorage.setItem(PREFERENCE_KEY, "beta");

    const destinationUrl = `${BETA_URL}${window.location.pathname}${window.location.search}${window.location.hash}`;
    postPreference({
      selected_version: "beta",
      source_app: "legacy",
      destination_url: destinationUrl,
      metadata: {
        trigger: "opt_in_prompt",
      },
    });

    window.location.assign(destinationUrl);
    return;
  }

  postPreference({
    selected_version: "legacy",
    source_app: "legacy",
    destination_url: `${LEGACY_URL}/`,
    metadata: {
      trigger: "opt_in_prompt_declined",
    },
  });
}

export function switchToBetaFromLegacy(extraMetadata = {}) {
  if (!isLegacyHost()) {
    return;
  }

  localStorage.setItem(PREFERENCE_KEY, "beta");
  localStorage.setItem(PROMPT_SHOWN_KEY, "true");

  const destinationUrl = `${BETA_URL}${window.location.pathname}${window.location.search}${window.location.hash}`;
  postPreference({
    selected_version: "beta",
    source_app: "legacy",
    destination_url: destinationUrl,
    metadata: {
      trigger: "manual_switch_from_profile",
      ...sanitizeMetadata(extraMetadata),
    },
  });

  window.location.assign(destinationUrl);
}

export function goToLegacyFromBeta(extraMetadata = {}) {
  const destinationUrl = `${LEGACY_URL}/?${PREFERENCE_PARAM}=legacy`;

  postPreference({
    selected_version: "legacy",
    source_app: "beta",
    destination_url: destinationUrl,
    metadata: {
      trigger: "manual_switch",
      ...sanitizeMetadata(extraMetadata),
    },
  });

  window.location.assign(destinationUrl);
}
